import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class ApiUrlService {
  private readonly baseUrl = environment.urlApiEndPointV3.replace(/\/$/, '');
  private readonly adminChatBasePath = '/api/v1/admin/chat';
  private readonly adminPanelBasePath = '/api/v1/admin/panel-administracion';
  private readonly adminPanelActionsBasePath = `${this.adminPanelBasePath}/actions`;
  private readonly adminPanelClientsBasePath = `${this.adminPanelBasePath}/clients`;
  private readonly chatSocketPath = '/ws/chat';

  get apiBase(): string {
    return this.baseUrl;
  }

  build(path: string): string {
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    return `${this.baseUrl}${normalizedPath}`;
  }

  buildWebSocket(path: string, queryParams: Record<string, string | number | boolean | null | undefined> = {}): string {
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    const wsBase = this.baseUrl.replace(/^http:/i, 'ws:').replace(/^https:/i, 'wss:');
    const url = new URL(`${wsBase}${normalizedPath}`);

    Object.entries(queryParams).forEach(([key, value]) => {
      if (value === null || value === undefined) return;
      url.searchParams.set(key, String(value));
    });

    return url.toString();
  }

  get loginUserApiEnd(): string {
    return this.build('/users/user-info-recover');
  }

  get tokenUserApiEnd(): string {
    return this.build('/token');
  }

  get statusUserApiEnd(): string {
    return this.build('/status');
  }

  get me(): string {
    return this.build('/api/v1/me');
  }

  get adminChatBase(): string {
    return this.build(this.adminChatBasePath);
  }

  get adminChatUnreadCount(): string {
    return this.build(`${this.adminChatBasePath}/unread-count`);
  }

  get adminChatUnreadCountByUser(): string {
    return this.build(`${this.adminChatBasePath}/unread-count/by-user`);
  }

  get adminChatConversations(): string {
    return this.build(`${this.adminChatBasePath}/conversations`);
  }

  get adminChatMessages(): string {
    return this.build(`${this.adminChatBasePath}/messages`);
  }

  get adminChatMessagesSearch(): string {
    return this.build(`${this.adminChatBasePath}/messages/search`);
  }

  get adminChatMessagesWithAttachments(): string {
    return this.build(`${this.adminChatBasePath}/messages/with-attachments`);
  }

  get adminChatRead(): string {
    return this.build(`${this.adminChatBasePath}/read`);
  }

  get adminChatUnread(): string {
    return this.build(`${this.adminChatBasePath}/unread`);
  }

  adminChatMessage(idMessage: number | string): string {
    return this.build(`${this.adminChatBasePath}/messages/${idMessage}`);
  }

  adminChatAttachmentDownloadUrl(idAttachment: number | string): string {
    return this.build(`${this.adminChatBasePath}/attachments/${idAttachment}/download-url`);
  }

  adminChatAttachmentContent(idAttachment: number | string): string {
    return this.build(`${this.adminChatBasePath}/attachments/${idAttachment}/content`);
  }

  get adminPanelBase(): string {
    return this.build(this.adminPanelBasePath);
  }

  get adminPanelActivityStatus(): string {
    return this.build(`${this.adminPanelBasePath}/activity/status`);
  }

  get adminPanelRequestsVolume(): string {
    return this.build(`${this.adminPanelBasePath}/activity/requests/volume`);
  }

  get adminPanelBudgetsVolume(): string {
    return this.build(`${this.adminPanelBasePath}/activity/budgets/volume`);
  }

  get adminPanelRequestsTopClients(): string {
    return this.build(`${this.adminPanelBasePath}/activity/requests/top-clients`);
  }

  get adminPanelBudgetsTopClients(): string {
    return this.build(`${this.adminPanelBasePath}/activity/budgets/top-clients`);
  }

  get adminPanelRequestsLog(): string {
    return this.build(`${this.adminPanelBasePath}/activity/requests/log`);
  }

  get adminPanelBudgetsLog(): string {
    return this.build(`${this.adminPanelBasePath}/activity/budgets/log`);
  }

  get adminPanelRequiredActions(): string {
    return this.build(`${this.adminPanelBasePath}/activity/required-actions`);
  }

  get adminPanelActionsBase(): string {
    return this.build(this.adminPanelActionsBasePath);
  }

  get adminPanelActionRules(): string {
    return this.build(`${this.adminPanelActionsBasePath}/rules`);
  }

  adminPanelActionRule(ruleKey: string): string {
    return this.build(`${this.adminPanelActionsBasePath}/rules/${encodeURIComponent(ruleKey)}`);
  }

  get adminPanelActionQueue(): string {
    return this.build(`${this.adminPanelActionsBasePath}/queue`);
  }

  adminPanelActionDetail(actionKey: string): string {
    return this.build(`${this.adminPanelActionsBasePath}/queue/${encodeURIComponent(actionKey)}`);
  }

  adminPanelActionResolve(actionKey: string): string {
    return this.build(`${this.adminPanelActionsBasePath}/${encodeURIComponent(actionKey)}/resolve`);
  }

  adminPanelActionIgnore(actionKey: string): string {
    return this.build(`${this.adminPanelActionsBasePath}/${encodeURIComponent(actionKey)}/ignore`);
  }

  adminPanelActionReopen(actionKey: string): string {
    return this.build(`${this.adminPanelActionsBasePath}/${encodeURIComponent(actionKey)}/reopen`);
  }

  get adminPanelActionClients(): string {
    return this.build(`${this.adminPanelActionsBasePath}/clients`);
  }

  get adminPanelClients(): string {
    return this.build(this.adminPanelClientsBasePath);
  }

  get adminPanelClientsSummary(): string {
    return this.build(`${this.adminPanelClientsBasePath}/summary`);
  }

  adminPanelClientDashboard(userId: string | number): string {
    return this.build(`${this.adminPanelClientsBasePath}/${encodeURIComponent(String(userId))}/dashboard`);
  }

  adminPanelClientScoring(userId: string | number): string {
    return this.build(`${this.adminPanelClientsBasePath}/${encodeURIComponent(String(userId))}/scoring`);
  }

  adminPanelActionClientResolve(clientId: string | number): string {
    return this.build(`${this.adminPanelActionsBasePath}/clients/${encodeURIComponent(String(clientId))}/resolve`);
  }

  adminPanelActionClientIgnore(clientId: string | number): string {
    return this.build(`${this.adminPanelActionsBasePath}/clients/${encodeURIComponent(String(clientId))}/ignore`);
  }

  adminPanelActionClientReopen(clientId: string | number): string {
    return this.build(`${this.adminPanelActionsBasePath}/clients/${encodeURIComponent(String(clientId))}/reopen`);
  }

  adminPanelRequiredActionResolve(actionKey: string): string {
    return this.build(
      `${this.adminPanelBasePath}/activity/required-actions/${encodeURIComponent(actionKey)}/resolve`
    );
  }

  adminPanelRequiredActionIgnore(actionKey: string): string {
    return this.build(
      `${this.adminPanelBasePath}/activity/required-actions/${encodeURIComponent(actionKey)}/ignore`
    );
  }

  adminPanelRequiredActionReopen(actionKey: string): string {
    return this.build(
      `${this.adminPanelBasePath}/activity/required-actions/${encodeURIComponent(actionKey)}/reopen`
    );
  }

  chatWebSocketUrl(accessToken: string): string {
    return this.buildWebSocket(this.chatSocketPath, { token: accessToken });
  }
}
